import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Spinner } from "reactstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import BootstrapTheme from "@fullcalendar/bootstrap";
import mapEvents from "./MapEvents";
import { getBookings } from "helpers/API Calls/Operations/booking";
import { useNavigate } from "react-router-dom";
import renderEventContent from "./RenderEvents";
import Breadcrumb from "Components/UI/Breadcrumb";

const normalizeDate = (date: string) => {
  return new Date(date).toISOString().split("T")[0]; // Ensures YYYY-MM-DD format
};

const Calender = () => {

    const navigate = useNavigate(); 

  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [selectedDateRange, setSelectedDateRange] = useState<{ start: string; end: string } | null>(null);
  const [fetchedRanges, setFetchedRanges] = useState<{ start: string; end: string }[]>([]);

  const isRangeFetched = (start: string, end: string) => {
    return fetchedRanges.some(
      (range) => new Date(start) >= new Date(range.start) && new Date(end) <= new Date(range.end)
    );
  };  
  
  const mergeFetchedRanges = (newStart: string, newEnd: string) => {
    let updatedRanges = [...fetchedRanges, { start: newStart, end: newEnd }];
    
    // Merge overlapping ranges
    updatedRanges.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    
    let mergedRanges: { start: string; end: string }[] = [];
    updatedRanges.forEach((range) => {
      if (mergedRanges.length === 0) {
        mergedRanges.push(range);
      } else {
        let lastRange = mergedRanges[mergedRanges.length - 1];
        if (new Date(lastRange.end) >= new Date(range.start)) {
          lastRange.end = new Date(Math.max(new Date(lastRange.end).getTime(), new Date(range.end).getTime()))
            .toISOString()
            .split("T")[0];
        } else {
          mergedRanges.push(range);
        }
      }
    });
  
    setFetchedRanges(mergedRanges);
  };

  const fetchCalendarData = async (start: string, end: string) => {
    if (isRangeFetched(start, end)) return;
  
    setIsLoading(true);
    try {
      const response = await getBookings(`limit=10000&booking_status_id=2&start_date=${start}&end_date=${end}`);
      if (response) {
        const formattedEvents = mapEvents(response.data.items);
        setData((prevData) => [...prevData, ...formattedEvents]);
        mergeFetchedRanges(start, end); // Store merged fetched range
      }
    } catch (error) {
      console.error("Failed to fetch calendar data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDatesSet = (dateInfo: any) => {
    const newStart = normalizeDate(dateInfo.startStr);
    const newEnd = normalizeDate(dateInfo.endStr);
  
    // Prevent updating if the range is already fetched
    if (isRangeFetched(newStart, newEnd)) {
      console.log("Skipping update - range already fetched.");
      return;
    }
  
    setSelectedDateRange({ start: newStart, end: newEnd });
  };
  
  useEffect(() => {
    if (!selectedDateRange) return; // Prevents calling API until range is ready
    fetchCalendarData(selectedDateRange.start, selectedDateRange.end);
  }, [selectedDateRange]); 
  
  
  const handleEventClick = (clickInfo: any) => {
    const id = clickInfo.event.extendedProps.id;
    navigate(`/bookings/${id}/edit`)
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Bookings Calendar", link: "/bookings" }]} />
        
        <Card>
          <CardBody>
            {isLoading && (
              <Spinner color="primary" 
              className="position-absolute" style={{ zIndex: 1000, top: '50%', left: '50%' }} />
            )}
            <FullCalendar              
              timeZone="UTC"
              plugins={[BootstrapTheme, dayGridPlugin, listPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              handleWindowResize
              themeSystem="bootstrap"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek',
              }}              
              views={{
                listWeek: {
                  displayEventTime: false,
                  displayEventEnd: false
                }
              }}
              events={data}
              editable={false}
              selectable={true}
              eventContent={renderEventContent}
              eventClick ={handleEventClick}
              datesSet={handleDatesSet}
              height="auto"
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Calender;
